import React, { useState } from 'react';
import { useTheme } from 'styled-components';

import { LinhaLegenda, Marcador, RoscaCentro, RoscaContainer, RoscaGrafico, RoscaLegenda } from './styles';
import { agruparOutros } from './escala';
import { Formato, ItemValor } from '../../types/Dashboard';
import { formatarValor } from '../../utils/formatadores';

const TAMANHO = 200;
const ESPESSURA = 30;

function arco(cx: number, cy: number, r: number, ini: number, fim: number) {
  // ângulos em radianos, 0 = topo, sentido horário
  const p = (a: number) => [cx + r * Math.sin(a), cy - r * Math.cos(a)];
  const [x1, y1] = p(ini);
  const [x2, y2] = p(fim);
  const grande = fim - ini > Math.PI ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${grande} 1 ${x2} ${y2}`;
}

function percentual(valor: number, total: number, casas: number) {
  return `${((valor / total) * 100).toFixed(casas).replace('.', ',')}%`;
}

// Composição de um total. Passe o mouse (ou toque) numa fatia ou linha da legenda para ver o valor no centro.
export default function Rosca({ itens: brutos, formato }: { itens: ItemValor[]; formato: Formato }) {
  const theme = useTheme();
  const [sel, setSel] = useState<number | null>(null);

  const itens = agruparOutros(brutos.filter(i => i.valor > 0));
  const total = itens.reduce((s, i) => s + i.valor, 0);
  if (total <= 0) return null;

  const c = TAMANHO / 2;
  const r = (TAMANHO - ESPESSURA - 8) / 2; // folga para a fatia selecionada, que engrossa 6px
  let acumulado = 0;
  const fatias = itens.map((item, i) => {
    const ini = (acumulado / total) * Math.PI * 2;
    acumulado += item.valor;
    const fim = (acumulado / total) * Math.PI * 2;
    // fatia de 100%: dois meio-arcos (um arco SVG não fecha o círculo)
    const d = itens.length === 1
      ? `${arco(c, c, r, 0, Math.PI)} ${arco(c, c, r, Math.PI, Math.PI * 2 - 0.0001)}`
      : arco(c, c, r, ini, fim);
    return { item, cor: theme.cores.serie[i], d, i, ini };
  });

  const centro = sel !== null ? itens[sel] : null;
  const alternar = (i: number) => setSel(sel === i ? null : i);

  return (
    <RoscaContainer onMouseLeave={() => setSel(null)}>
      <RoscaGrafico style={{ width: TAMANHO, height: TAMANHO }}>
        <svg width={TAMANHO} height={TAMANHO} role='img' aria-label={`Total ${formatarValor(total, formato)}`}>
          {fatias.map(f => (
            <path
              key={f.i}
              d={f.d}
              stroke={f.cor}
              strokeWidth={sel === f.i ? ESPESSURA + 6 : ESPESSURA}
              opacity={sel === null || sel === f.i ? 1 : 0.35}
              fill='none'
              onMouseEnter={() => setSel(f.i)}
              onClick={() => alternar(f.i)}
            />
          ))}
          {/* separador de 2px na cor da superfície entre as fatias */}
          {itens.length > 1 && fatias.map(f => {
            const r1 = r - ESPESSURA / 2 - 4;
            const r2 = r + ESPESSURA / 2 + 4;
            return (
              <path
                key={`sep${f.i}`}
                d={`M ${c + r1 * Math.sin(f.ini)} ${c - r1 * Math.cos(f.ini)} L ${c + r2 * Math.sin(f.ini)} ${c - r2 * Math.cos(f.ini)}`}
                stroke={theme.cores.card}
                strokeWidth={2}
                pointerEvents='none'
              />
            );
          })}
        </svg>
        <RoscaCentro>
          <span>{centro ? centro.rotulo : 'Total'}</span>
          <strong>{formatarValor(centro ? centro.valor : total, formato)}</strong>
          {centro && <span>{percentual(centro.valor, total, 1)}</span>}
        </RoscaCentro>
      </RoscaGrafico>

      {/* legenda com valores: também é a leitura acessível do gráfico */}
      <RoscaLegenda>
        {fatias.map(f => (
          <LinhaLegenda
            key={f.i}
            $ativo={sel === f.i}
            onMouseEnter={() => setSel(f.i)}
            onClick={() => alternar(f.i)}
          >
            <Marcador $cor={f.cor} />
            <span className='rotulo' title={f.item.rotulo}>{f.item.rotulo}</span>
            <span className='valor'>{formatarValor(f.item.valor, formato)}</span>
            <span className='percentual'>{percentual(f.item.valor, total, 0)}</span>
          </LinhaLegenda>
        ))}
      </RoscaLegenda>
    </RoscaContainer>
  );
}
